import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Chip,
    Divider,
    Button,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useNewsDetailController } from "./context/news-detail-controller";
import { useNewsDetailState } from "./context/news-detail-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

export function NewsDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const { loadNewsDetail } = useNewsDetailController();
    const { newsDetail } = useNewsDetailState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadNewsDetail(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleBackToNews = () => {
        navigate('/noticias');
    };

    const handleShare = (platform: string) => {
        const currentUrl = window.location.href;
        const text = encodeURIComponent(newsDetail?.title || '');

        let shareUrl = '';

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?url=${currentUrl}&text=${text}`;
                break;
            case 'instagram':
                // Instagram não suporta compartilhamento direto via URL
                // Copia o link para a área de transferência
                navigator.clipboard.writeText(currentUrl);
                alert('Link copiado! Cole no Instagram para compartilhar.');
                return;
            default:
                return;
        }

        window.open(shareUrl, '_blank', 'width=600,height=400');
    };

    return (
        <Box>
            <PageHeader
                title={newsDetail?.title || 'Carregando...'}
                subtitle=""
                breadcrumbs={[
                    { label: 'Notícias', href: '/noticias' },
                    { label: newsDetail?.title || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                {newsDetail?.category && (
                                    <Chip
                                        label={newsDetail.category}
                                        color="primary"
                                    />
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {newsDetail?.publishedDate
                                            ? dayjs(newsDetail.publishedDate).format('DD/MM/YYYY')
                                            : dayjs(newsDetail?.date).format('DD/MM/YYYY')}
                                    </Typography>
                                </Box>
                                {newsDetail?.desFont && (
                                    <>
                                        <Divider orientation="vertical" flexItem />
                                        <Typography variant="body2" color="text.secondary">
                                            Fonte: {newsDetail.desFont}
                                        </Typography>
                                    </>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShareIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Compartilhar:
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleShare('facebook')}
                                    sx={{
                                        minWidth: 'auto',
                                        p: 0.5,
                                        borderColor: '#1877F2',
                                        color: '#1877F2',
                                        '&:hover': {
                                            borderColor: '#1877F2',
                                            backgroundColor: 'rgba(24, 119, 242, 0.1)',
                                        }
                                    }}
                                >
                                    <FacebookIcon sx={{ fontSize: '1.2rem' }} />
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleShare('twitter')}
                                    sx={{
                                        minWidth: 'auto',
                                        p: 0.5,
                                        borderColor: '#1DA1F2',
                                        color: '#1DA1F2',
                                        '&:hover': {
                                            borderColor: '#1DA1F2',
                                            backgroundColor: 'rgba(29, 161, 242, 0.1)',
                                        }
                                    }}
                                >
                                    <TwitterIcon sx={{ fontSize: '1.2rem' }} />
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleShare('instagram')}
                                    sx={{
                                        minWidth: 'auto',
                                        p: 0.5,
                                        borderColor: '#E4405F',
                                        color: '#E4405F',
                                        '&:hover': {
                                            borderColor: '#E4405F',
                                            backgroundColor: 'rgba(228, 64, 95, 0.1)',
                                        }
                                    }}
                                >
                                    <InstagramIcon sx={{ fontSize: '1.2rem' }} />
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    <Paper sx={{ p: 4 }} elevation={2}>
                        {newsDetail?.urlImageEmphasis && !imageError ? (
                            <Box
                                component="img"
                                src={newsDetail.urlImageEmphasis}
                                alt={newsDetail.title}
                                onError={() => setImageError(true)}
                                sx={{
                                    width: '100%',
                                    maxHeight: 500,
                                    objectFit: 'cover',
                                    borderRadius: 2,
                                    mb: 3,
                                }}
                            />
                        ) : imageError ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 400,
                                    backgroundColor: 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    mb: 3,
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    Sem imagem
                                </Typography>
                            </Box>
                        ) : null}



                        <Divider sx={{ my: 3 }} />

                        <Box
                            sx={{
                                '& p': {
                                    mb: 2,
                                    lineHeight: 1.8,
                                    fontSize: '1.05rem',
                                },
                                '& img': {
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: 1,
                                    my: 2,
                                },
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    mt: 3,
                                    mb: 2,
                                    fontWeight: 600,
                                },
                                '& ul, & ol': {
                                    mb: 2,
                                    pl: 3,
                                },
                                '& li': {
                                    mb: 1,
                                    lineHeight: 1.8,
                                },
                                '& blockquote': {
                                    borderLeft: '4px solid',
                                    borderColor: 'primary.main',
                                    pl: 2,
                                    py: 1,
                                    my: 2,
                                    fontStyle: 'italic',
                                    backgroundColor: 'grey.50',
                                },
                            }}
                        >
                            <Typography
                                component="div"
                                dangerouslySetInnerHTML={{ __html: newsDetail?.text || '' }}
                            />
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToNews}
                                size="large"
                            >
                                Voltar para notícias
                            </Button>
                        </Box>
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
